<template>
  <v-app id="app" :class="appClass" :dir="direction">
    <NavigationBar />
    <v-main>
      <v-dialog
        :model-value="isLogoutWarningVisible"
        persistent
        max-width="520"
        role="alert"
        data-test-id="logout-warning-dialog"
        @update:model-value="handleLogoutWarningVisibilityChange"
      >
        <v-card class="dialog-warning">
          <v-card-text data-test-id="logoutWarningMessage">
            {{ $t('LogoutWarningText') }}
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="white"
              variant="flat"
              data-test-id="logoutWarningAction"
              @click="dismissLogoutWarning"
            >
              {{ $t('LogoutWarningAction') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <router-view class="view"></router-view>
      <NotificationHost />
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, watch, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useLocale } from 'vuetify'
import AuthEngine from '@/plugins/msal'
import NavigationBar from './components/NavigationBar.vue'
import NotificationHost from './components/NotificationHost.vue'
import { useAppStore } from '@/stores/app'
import { getEnvironmentBackgroundClass } from '@/utils/environmentClass'

const route = useRoute()
const { locale } = useI18n()
const vuetifyLocale = useLocale()
const store = useAppStore()
const isLogoutWarningDismissed = ref(false)

onMounted(() => {
  if (route.path.toLowerCase() !== '/callback') {
    AuthEngine.renewTokens().catch(() => {
      // Suppress the 'not logged in' error as we can illegitimately get that
      // when processing the callback url
    })
  }
})

const appClass = computed(() => {
  return getEnvironmentBackgroundClass(import.meta.env.VITE_ENV)
})

const direction = computed(() => {
  const currentLocale = (locale.value || '').toString().toLowerCase()
  return currentLocale.startsWith('he') ? 'rtl' : 'ltr'
})

watchEffect(() => {
  document.documentElement.lang = locale.value || 'en'
  document.documentElement.dir = direction.value
  vuetifyLocale.current.value = locale.value || 'en'
})

const userMsToExpire = computed(() => store.getUserMsToExpire)

const shouldShowExpireUserMessage = computed(() => {
  let showMessage = false
  const remainingMsForLogoutWarning = 5 * 60 * 1000
  const msToLogoutWarning = (userMsToExpire.value || 0) - remainingMsForLogoutWarning
  if (userMsToExpire.value && msToLogoutWarning <= 0) {
    showMessage = true
  }
  return showMessage
})

const isLogoutWarningVisible = computed(() => {
  return shouldShowExpireUserMessage.value && !isLogoutWarningDismissed.value
})

const dismissLogoutWarning = () => {
  isLogoutWarningDismissed.value = true
}

const handleLogoutWarningVisibilityChange = (isVisible: boolean) => {
  if (!isVisible) {
    dismissLogoutWarning()
  }
}

watch(shouldShowExpireUserMessage, (shouldShow) => {
  if (!shouldShow) {
    isLogoutWarningDismissed.value = false
  }
})

</script>

<style lang="scss">
// Font Awesome SVG icon fixes for app-level notification icons.
.icon svg {
  fill: currentColor !important;
  width: 1em;
  height: 1em;
  vertical-align: middle;
}

.icon svg path {
  fill: inherit;
}

</style>
